// NOTICE: This file was copied from the oppizi-shared repository, do
// not modify it here.

import { CHAT_MESSAGE_TYPES, CHAT_STRING_TYPES } from './constants'

/**
 * Formats conversations so they can be consumed by the UI
 *
 * @param {Array} paramObj.conversations - Raw conversations received from WS
 * @param {Array} paramObj.messages - Raw messages received from WS
 * @param {Boolean} paramObj.isEndUser - Indicator of user's role
 * @param {String} paramObj.defaultConversationName - If conversations doesn't have a name this will be used
 * @returns {Array} - Formatted conversations ready to be consumed by the UI
 *
 * @example
 * formatRawConversations({
 *    conversations: [
 *      {
 *         id: Number,
 *         name: String,
 *         lastReadTime: String,
 *         archived: Boolean,
 *         isCityBased: Boolean,
 *         orderedMessages: Array<String>,
 *      },
 *    ],
 *    messages: [
 *      {
 *         id: Number,
 *         userId: Number,
 *         conversationId: Number,
 *         timestamp: String,
 *         payload: {
 *           type: String,
 *           text: String,
 *         },
 *      },
 *    ],
 *  })
 */
export const formatRawConversations = ({
  conversations,
  messages,
  isEndUser,
  defaultConversationName = 'Pending conversation',
}) => {
  if (!conversations) return []
  const lastReadTimeProp = isEndUser ? 'lastReadTime' : 'lastStaffReadTime'

  const orderedConversationsData = conversations.sort((a, b) => {
    const conversationALastMessageTimestamp =
      messages[a.orderedMessages[0]] && messages[a.orderedMessages[0]].timestamp
    const conversationBLastMessageTimestamp =
      messages[b.orderedMessages[0]] && messages[b.orderedMessages[0]].timestamp

    const isUnreadA = a[lastReadTimeProp] < conversationALastMessageTimestamp
    const isUnreadB = b[lastReadTimeProp] < conversationBLastMessageTimestamp

    if (isUnreadA && !isUnreadB) {
      return -1
    } else if (!isUnreadA && isUnreadB) {
      return 1
    } else {
      return (
        conversationBLastMessageTimestamp - conversationALastMessageTimestamp
      )
    }
  })

  return orderedConversationsData.map(conversation => {
    const conversationMessages = Object.values(messages).filter(
      message => conversation.id === message.conversationId,
    )
    const message = conversationMessages[conversationMessages.length - 1]

    let messageText = ''
    if (message) {
      if (message.payload.type === CHAT_MESSAGE_TYPES.TEXT) {
        messageText = message && message.payload.text
      } else if (message.payload.type === CHAT_MESSAGE_TYPES.LOCATION) {
        messageText = 'Location'
      } else {
        messageText = 'Media file'
      }
    }

    return {
      ...conversation,
      id: conversation.id,
      name: conversation.name || defaultConversationName,
      lastMessageText: messageText,
      timestamp: message && message.timestamp,
      isUnread: message && conversation[lastReadTimeProp] < message.timestamp,
      isCityBased: conversation.isCityBased,
      archived: conversation.archived,
      orderedMessages: conversation.orderedMessages,
    }
  })
}

export const searchConversationsByName = ({ searchedValue, data }) => {
  const lowerCasedSearchedValue = searchedValue.trim().toLowerCase()
  return data.filter(conversation => {
    const lowerCasedConversationName = conversation.name.toLowerCase()
    return lowerCasedConversationName.includes(lowerCasedSearchedValue)
  })
}

export const getUnreadConversations = ({
  conversations,
  messages,
  isEndUser,
}) => {
  if (!conversations) return []
  const lastReadTimeProp = isEndUser ? 'lastReadTime' : 'lastStaffReadTime'
  return conversations.filter(conversation => {
    if (
      !conversation ||
      !conversation.orderedMessages ||
      !conversation.orderedMessages[0]
    ) {
      return false
    }

    const conversationLastMessageTimestamp =
      messages[conversation.orderedMessages[0]].timestamp

    return (
      !conversation.archived &&
      conversation[lastReadTimeProp] < conversationLastMessageTimestamp
    )
  })
}

export const checkIfChatHaveUnreads = ({
  conversations,
  messages,
  isEndUser,
}) => {
  const unreadConversations = getUnreadConversations({
    conversations,
    messages,
    isEndUser,
  })
  return unreadConversations.length > 0
}

export const isAlreadyInConversationWithUser = ({
  conversations,
  participantsIds,
  participantsType,
}) => {
  const participantsIdsStr = Array.from(participantsIds)
    .sort()
    .join(',')

  const existingConversationId = Object.values(conversations)
    .filter(conversation => !conversation.archived && !conversation.missionId)
    .reduce((acc, conversation) => {
      const idsType =
        participantsType === 'CITY' ? 'cityBasedCityIds' : 'participantsIds'

      if (conversation[idsType] && conversation[idsType].length > 0) {
        const conversationParticipantsIdsStr = Array.from(conversation[idsType])
          .sort()
          .join(',')

        if (conversationParticipantsIdsStr === participantsIdsStr) {
          acc = conversation.id
        }
      }
      return acc
    }, null)

  return existingConversationId
}

/**
 * Parses message text using regex and returns array of items with data
 * that indicates whether item is simple text or URL
 * @param {String} params.text
 */
export const parseMessage = ({ text }) => {
  /* eslint-disable */
  // Source: https://stackoverflow.com/a/19696443/5862368
  const urlRegex = /((?:(http|https|Http|Https|rtsp|Rtsp):\/\/(?:(?:[a-zA-Z0-9\$\-\_\.\+\!\*\'\(\)\,\;\?\&\=]|(?:\%[a-fA-F0-9]{2})){1,64}(?:\:(?:[a-zA-Z0-9\$\-\_\.\+\!\*\'\(\)\,\;\?\&\=]|(?:\%[a-fA-F0-9]{2})){1,25})?\@)?)?((?:(?:[a-zA-Z0-9][a-zA-Z0-9\-]{0,64}\.)+(?:(?:aero|arpa|asia|a[cdefgilmnoqrstuwxz])|(?:biz|b[abdefghijmnorstvwyz])|(?:cat|com|coop|c[acdfghiklmnoruvxyz])|d[ejkmoz]|(?:edu|e[cegrstu])|f[ijkmor]|(?:gov|g[abdefghilmnpqrstuwy])|h[kmnrtu]|(?:info|int|i[delmnoqrst])|(?:jobs|j[emop])|k[eghimnrwyz]|l[abcikrstuvy]|(?:mil|mobi|museum|m[acdghklmnopqrstuvwxyz])|(?:name|net|n[acefgilopruz])|(?:org|om)|(?:pro|p[aefghklmnrstwy])|qa|r[eouw]|s[abcdeghijklmnortuvyz]|(?:tel|travel|t[cdfghjklmnoprtvwz])|u[agkmsyz]|v[aceginu]|w[fs]|y[etu]|z[amw]))|(?:(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9])\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[0-9])))(?:\:\d{1,5})?)(\/(?:(?:[a-zA-Z0-9\;\/\?\:\@\&\=\#\~\-\.\+\!\*\'\(\)\,\_])|(?:\%[a-fA-F0-9]{2}))*)?(?:\b|$)/gi
  /* eslint-enable */
  const splittedText = text.split(' ')
  return splittedText.map(textPart => {
    const isUrl = Boolean(textPart.match(urlRegex))
    return {
      value: `${textPart} `,
      type: isUrl ? CHAT_STRING_TYPES.URL : CHAT_STRING_TYPES.TEXT,
    }
  })
}
